import type { RegisterResponse } from '@ffs/shared';
import type { ComponentChildren } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { useSlots } from '../hooks/useSlots';
import { ApiError, getReschedule, postReschedule, type RescheduleInfo } from '../lib/api';
import { fireRegistered } from '../lib/events';
import type { MountOptions } from '../lib/options';
import { detectTimezone } from '../lib/tz';
import { mapServerError, SLOT_KEY } from '../lib/validate';
import { Notice, RegisteredConfirmation, Shell } from './App';
import { choiceOffered, defaultChoice, type SlotChoice, SlotPicker } from './SlotPicker';

/** Reschedule mode (FR-REG-10): `<div data-ffs-reschedule="<join token>">`. */
export function RescheduleApp({ o, token }: { o: MountOptions; token: string }) {
  const detectedTz = useMemo(() => detectTimezone(), []);
  const [tz, setTz] = useState(detectedTz);
  const [info, setInfo] = useState<RescheduleInfo | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [choice, setChoice] = useState<SlotChoice | null>(null);
  const [slotError, setSlotError] = useState<string | undefined>();
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<RegisterResponse | null>(null);

  const fetcher = useCallback(
    async (z: string) => {
      try {
        return await getReschedule(o.base, token, z);
      } catch (e) {
        if (e instanceof ApiError && e.status !== 0 && e.status < 500)
          setFatal(
            e.code === 'not_found'
              ? 'This link is not valid. Please use the link from your confirmation email.'
              : e.message,
          );
        throw e;
      }
    },
    [o.base, token],
  );
  const { slots, loading, error, reload } = useSlots(fetcher, tz, setInfo);

  useEffect(() => {
    if (!slots || choiceOffered(slots, choice)) return;
    setChoice(defaultChoice(slots));
  }, [slots, choice]);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (submitting) return;
    setMessage(null);
    if (choice === null) {
      setSlotError(slots?.length ? 'Choose a time' : 'No other times are available');
      return;
    }
    setSubmitting(true);
    try {
      const res = await postReschedule(o.base, token, choice, tz);
      fireRegistered(res);
      setResult(res);
    } catch (err) {
      const m = mapServerError(
        err instanceof ApiError ? err : new ApiError(0, 'http_error', 'Something went wrong'),
      );
      if (m.action === 'refresh_slots') void reload();
      setSlotError(m.fields[SLOT_KEY]);
      setMessage(m.message);
    } finally {
      setSubmitting(false);
    }
  };

  let body: ComponentChildren;
  if (result) body = <RegisteredConfirmation res={result} />;
  else if (fatal) body = <Notice>{fatal}</Notice>;
  else
    body = (
      <form onSubmit={onSubmit} noValidate aria-busy={submitting}>
        {info ? (
          <p class="current">
            {info.webinar_title}
            <br />
            Currently booked for <strong>{info.current_label}</strong>
          </p>
        ) : (
          <div class="skel" style="margin-bottom:16px" />
        )}
        <SlotPicker
          heading="Pick a new time"
          slots={slots}
          loading={loading}
          error={error}
          fieldError={slotError}
          tz={tz}
          detectedTz={detectedTz}
          selected={choice}
          disabled={submitting}
          onSelect={(c) => {
            setChoice(c);
            setSlotError(undefined);
          }}
          onTzChange={setTz}
        />
        <div class="alert" role="alert" aria-live="assertive">
          {message}
        </div>
        <button
          class="btn"
          type="submit"
          disabled={submitting || (slots !== null && slots.length === 0)}
        >
          {submitting ? 'Moving…' : 'Confirm new time'}
        </button>
      </form>
    );

  return (
    <Shell accent={null} hosted={o.hosted || o.flat}>
      {body}
    </Shell>
  );
}
